import {Component, effect, inject, input, Input, output, viewChildren, ViewEncapsulation} from '@angular/core';
import {map} from 'rxjs';
import {patchState} from '@ngrx/signals';
import {MatProgressBar} from '@angular/material/progress-bar';
import {
  ECalculateImportValue,
  EControlActions,
  IControlDataChanged,
  SignalControl,
} from 'frontier/nucleus';
import {mapRow} from './map-row';
import {TableVerticalControlCoreComponent} from './table-vertical-control-core/table-vertical-control-core.component';
import {CellEditorComponent} from './cell-editor/cell-editor.component';
import {ITableVerticalDataGroup} from './table-vertical-api-adapter';
import {IApiRow} from '../table-control/api-row.interface';
import {IApiTableData} from '../table-control/interfaces/api-table-data.interface';
import {ICellChangedEvent} from '../table-vertical-control/cell-editor/cell-changed-event.interface';
import {IGroupMap} from '../../../../../../nucleus/src/lib/control/base-control-new/signal-control.directive';
import {TableService} from '../../../../../../nucleus/src/lib/apiv2/generated/api/table.service';

@Component({
  selector: 'kpi4me-table-vertical-control',
  templateUrl: './table-vertical-control.component.html',
  styleUrls: ['./table-vertical-control.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [
    MatProgressBar,
    TableVerticalControlCoreComponent,
  ],
})
export class TableVerticalControlComponent extends SignalControl<IApiTableData, ITableVerticalDataGroup[]> {
  private _tableService = inject(TableService);
  @Input() header: string;
  hasImports = input<false>(false);
  showImports = input<boolean>(false);
  cellEditorRefs = viewChildren(CellEditorComponent);
  groups = this.data;
  showImportsChange = output<boolean>();
  cellChanged = output<ICellChangedEvent>();

  constructor() {
    super();

    // Seed the default before the instance is created so the effect below is a no-op on init
    // unless showImports is bound to true — otherwise every init pays a second, aborted fetch.
    this.patchFilter({calculateimportvalue: ECalculateImportValue.off});

    effect(() => {
      // Fetch new values only if both showImports has been changed to true and the imports haven't already been loaded
      const showImports = this.showImports();
      const apiInstance = this.apiInstance();
      if (apiInstance) {
        this.patchFilter({
          calculateimportvalue: showImports ? ECalculateImportValue.first : ECalculateImportValue.off
        })
      }
    }, {allowSignalWrites: true});
  }

  override toModel(_: IApiTableData) {
    return this.fetchRows();
  }

  fetchRows() {
    return this._tableService.tablePostFetchRows(
      {
        _parameters: [
          this.instanceId(),
          0,
          1,
          0,
          this.apiData().displayedColumns.length - 1
        ]
      }
    ).pipe(map((_) => {
      const rows = _ as IApiRow[];
      if (rows.length != 1) {
        console.warn(`Unexpected number of rows: ${rows.length}. Only expected one row for the client. Is the client filter applied?`);
      }
      return this.mapRow(rows[0]);
    }));
  }

  protected mapRow(row: IApiRow, groupMap: IGroupMap = this.groupMap ): ITableVerticalDataGroup[] {
    return mapRow(row, this.apiData().displayedColumns, this.apiInstance(), groupMap);
  }

  onCellChange(evt: { row: ICellChangedEvent, groupIdx: number}) {
    this.cellChanged.emit(evt.row);
    const mapped: ITableVerticalDataGroup[] = this.mapRow(evt.row.apiRow);

    patchState(this.state, state => {
      const groups: ITableVerticalDataGroup[] = state.data;
      const group = groups[evt.groupIdx];
      const newGroup = mapped[evt.groupIdx];

      group.rows.forEach((row) => {
        row.value = newGroup.rows[row.index].value;
        row.invalidMessage = newGroup.rows[row.index].invalidMessage;
        row.component?.updateRow(row);
        row.hidden = newGroup.rows[row.index].hidden
      })
      return {data: groups};
    });

    // update the error state of each cell editor: reset
    this.cellEditorRefs().forEach(cellEditor => {
      cellEditor.componentRef?.instance?.error?.set(null);
    });

    this.controlStore.controlDataChanged$.emit({GUID: this.GUID, changeType: EControlActions.changeLine});
  }

  onUpdateTable() {
    this.changeAndFetchInstance();
  }

  onUpdateRepositories() {
    this.changeAndFetchInstance();
  }

  onSelectionDataChange(evt: IControlDataChanged) {
    this.controlStore.controlDataChanged$.emit({changeType: evt.changeType, GUID: this.GUID});
  }
}
